export const ZONE_CHAT_SEARCH_FIELDS = [
  {
    id: 'item_type',
    labelKey: 'Item type',
    defaultLabel: 'Item type',
  },
  {
    id: 'sku',
    labelKey: 'SKU',
    defaultLabel: 'SKU',
  },
  {
    id: 'collection',
    labelKey: 'Collection',
    defaultLabel: 'Collection',
  },
  {
    id: 'stock_id',
    labelKey: 'Stock ID',
    defaultLabel: 'Stock ID',
  },
];

const SEARCH_FIELD_IDS = ZONE_CHAT_SEARCH_FIELDS.map((field) => field.id);

const SEARCH_FIELD_TOKEN_PATTERN = new RegExp(
  `(?:^|\\s)(${SEARCH_FIELD_IDS.join('|')})\\s+`,
  'gi'
);

export const getSlashMenuState = (value = '', cursorPos = 0) => {
  const textBeforeCursor = String(value).slice(0, cursorPos);
  const slashIndex = textBeforeCursor.lastIndexOf('/');

  if (slashIndex === -1) {
    return null;
  }

  if (slashIndex > 0 && !/\s/.test(textBeforeCursor[slashIndex - 1])) {
    return null;
  }

  const query = textBeforeCursor.slice(slashIndex + 1);
  if (/[\s\n]/.test(query)) {
    return null;
  }

  return {
    slashIndex,
    query: query.toLowerCase(),
  };
};

export const filterSlashMenuOptions = (options = [], query = '') =>
  (options || []).filter((option) => {
    if (!query) return true;
    const id = String(option?.id ?? '').toLowerCase();
    const label = String(option?.defaultLabel ?? '').toLowerCase();
    return id.includes(query) || label.includes(query);
  });

export const buildInputWithSlashField = (
  value = '',
  cursorPos = 0,
  slashIndex = 0,
  fieldId = ''
) => {
  const text = String(value);
  const textBeforeSlash = text.slice(0, slashIndex);
  const after = text.slice(cursorPos);
  const insertion = `${fieldId} `;

  const tokens = [];
  for (const match of textBeforeSlash.matchAll(SEARCH_FIELD_TOKEN_PATTERN)) {
    const id = match[1].toLowerCase();
    const tokenStart = match.index + (match[0].startsWith(' ') ? 1 : 0);
    tokens.push({
      tokenStart,
      valueStart: tokenStart + id.length + 1,
    });
  }

  let replaceStart = slashIndex;
  if (tokens.length) {
    const lastToken = tokens[tokens.length - 1];
    const queryBeforeSlash = textBeforeSlash
      .slice(lastToken.valueStart, slashIndex)
      .trim();

    if (!queryBeforeSlash) {
      replaceStart = lastToken.tokenStart;
    }
  }

  const before = text.slice(0, replaceStart);
  const nextValue = `${before}${insertion}${after}`;

  return {
    nextValue,
    nextCursorPos: before.length + insertion.length,
  };
};

export const ISSUE_FIELD_VALUE_PICKER_FIELDS = ['item_type', 'sku', 'collection'];

export const isIssueFieldValuePickerField = (fieldId) =>
  ISSUE_FIELD_VALUE_PICKER_FIELDS.includes(String(fieldId || '').toLowerCase());

export const getIssueRowItemName = (row) =>
  row?.item_name ?? row?.Item_name ?? row?.itemName ?? row?.item ?? '';

export const getIssueRowSku = (row) => row?.SKU ?? row?.sku ?? '';

export const getIssueRowCollection = (row) =>
  row?.collection_name ?? row?.Collection_name ?? row?.collection ?? '';

const parseIssueRowBalance = (row) => {
  const parsed = Number.parseFloat(row?.balance);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const isIssueRowWithBalance = (row) => parseIssueRowBalance(row) > 0;

const getBalanceIssueRows = (rows = []) =>
  (rows || []).filter(isIssueRowWithBalance);

const getUniqueIssueFieldValues = (rows = [], getValue) => {
  const seen = new Set();
  const items = [];

  (rows || []).forEach((row) => {
    const value = String(getValue(row)).trim();
    if (!value || value === '-') return;

    const key = value.toLowerCase();
    if (seen.has(key)) return;

    seen.add(key);
    items.push(value);
  });

  return items.sort((a, b) => a.localeCompare(b));
};

export const getUniqueIssueItemTypes = (rows = []) =>
  getUniqueIssueFieldValues(rows, getIssueRowItemName);

export const getUniqueIssueSkus = (rows = []) =>
  getUniqueIssueFieldValues(rows, getIssueRowSku);

export const getUniqueIssueCollections = (rows = []) =>
  getUniqueIssueFieldValues(rows, getIssueRowCollection);

const ISSUE_FIELD_VALUE_GETTERS = {
  item_type: getIssueRowItemName,
  sku: getIssueRowSku,
  collection: getIssueRowCollection,
};

const getIssueFieldValueGetter = (fieldId) =>
  ISSUE_FIELD_VALUE_GETTERS[String(fieldId || '').toLowerCase()] || null;

export const getIssueFieldValueCounts = (fieldId, rows = []) => {
  const getValue = getIssueFieldValueGetter(fieldId);
  if (!getValue) {
    return {};
  }

  const counts = {};
  getBalanceIssueRows(rows).forEach((row) => {
    const value = String(getValue(row)).trim();
    if (!value || value === '-') return;

    const key = value.toLowerCase();
    counts[key] = (counts[key] || 0) + 1;
  });

  return counts;
};

export const getSearchFieldValueMenuState = (value = '', cursorPos = 0) => {
  const textBeforeCursor = String(value).slice(0, cursorPos);
  const tokens = [];

  for (const match of textBeforeCursor.matchAll(SEARCH_FIELD_TOKEN_PATTERN)) {
    const fieldId = match[1].toLowerCase();
    const matchStart = match.index + (match[0].startsWith(' ') ? 1 : 0);

    tokens.push({
      fieldId,
      valueStart: matchStart + fieldId.length + 1,
    });
  }

  if (!tokens.length) {
    return null;
  }

  const activeToken = tokens[tokens.length - 1];
  const query = textBeforeCursor.slice(activeToken.valueStart, cursorPos);

  return {
    fieldId: activeToken.fieldId,
    query,
    valueStart: activeToken.valueStart,
    valueEnd: cursorPos,
  };
};

const getUniqueValuesForPickerField = (fieldId, issueRows = []) => {
  const getValue = getIssueFieldValueGetter(fieldId);
  return getValue
    ? getUniqueIssueFieldValues(getBalanceIssueRows(issueRows), getValue)
    : [];
};

export const getIssueFieldValueSuggestions = (
  fieldId,
  issueRows = [],
  query = ''
) => {
  if (!isIssueFieldValuePickerField(fieldId)) {
    return [];
  }

  const normalizedQuery = String(query).trim().toLowerCase();
  const values = getUniqueValuesForPickerField(fieldId, issueRows);

  if (!normalizedQuery) {
    return values;
  }

  return values.filter((value) =>
    value.toLowerCase().includes(normalizedQuery)
  );
};

export const buildInputWithFieldValue = (
  value = '',
  cursorPos = 0,
  menuState,
  selectedValue = ''
) => {
  const before = String(value).slice(0, menuState.valueStart);
  const after = String(value).slice(cursorPos);
  const insertion = `${selectedValue} `;
  const nextValue = `${before}${insertion}${after}`;

  return {
    nextValue,
    nextCursorPos: before.length + insertion.length,
  };
};

export const parseMessageSearchField = (text = '') => {
  const trimmed = String(text).trim();
  const match = trimmed.match(
    /^(?:\/)?(item_type|sku|collection|stock_id)\s+([\s\S]+)$/i
  );

  if (!match) {
    return {
      message: trimmed,
      searchField: null,
    };
  }

  return {
    searchField: match[1].toLowerCase(),
    message: match[2].trim(),
  };
};
